"use client";
import { IBaseLabelValue } from "@/interfaces";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "@/components";
import { cn } from "@/lib";
import { useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import { routes } from "@/routes";
import DownloadPdf, { DownloadPdfRef } from "../../pdfTemplates/downloadPdf";
import ResultContent from "../../resultContent";
import { extractTime, toJalali } from "@/lib/utils";
import DetailPlateInquiry from "../../detailPlateInquiry";
import DetailPlateInquiryPdf from "../../pdfTemplates/detailPlateInquiryPdf";
import Logo from "../../logo";
import logoImage from "@public/svg/logo-2.svg";
import { QRCodeCanvas } from "qrcode.react";
import VehiclesPaymentModal from "@/components/Payment/vehiclesPayment/vehiclePaymentModal";
import { usePurchaseFileMutation } from "@/hooks";
import { useSession } from "next-auth/react";
import { useModalStore } from "@/store";
import { useToastStore } from "@/store/toastStore";
import { IRawPurchasedPlateInquiryResData } from "@/interfaces/inquiries/vehicle";

type RawPlateResponse = Record<string, unknown> & {
  convertedPlateNum?: string;
  detachDate?: string;
  detail?: string;
  logOnServer?: boolean;
  numberingPlaceCode?: string;
  numberingPlaceDesc?: string;
  plateId?: string;
  plateNum?: string;
  plateStatus?: { description?: string; id?: string | number };
  status?: number;
  title?: string;
  type?: string;
};

const toDisplayValue = (value: unknown, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};

const PlateInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedPlateInquiryResData;
}) => {
  const { data: session } = useSession();
  const searchParams = useSearchParams();
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const inquiryPayload = (data as any)?.data ?? data ?? {};
  const responsePayload = inquiryPayload?.data ?? inquiryPayload;
  const plateInput = {
    national_code: toDisplayValue(
      inquiryPayload?.input?.national_code ||
        inquiryPayload?.input?.nationalCode ||
        (data as any)?.user_input?.national_code ||
        (data as any)?.user_input?.nationalCode,
    ),
  };
  const rawPlateRecords: RawPlateResponse[] = Array.isArray(responsePayload)
    ? responsePayload
    : responsePayload && typeof responsePayload === "object"
      ? Object.entries(responsePayload)
          .filter(([key]) => /^\d+$/.test(key))
          .map(([, value]) => value as RawPlateResponse)
      : [];
  const plateData = rawPlateRecords
    .filter((item) => Boolean(item?.convertedPlateNum || item?.plateId))
    .map((item) => ({
      convertedPlateNum: toDisplayValue(item.convertedPlateNum),
      detachDate: toDisplayValue(item.detachDate, ""),
      numberingPlaceCode: toDisplayValue(item.numberingPlaceCode),
      numberingPlaceDesc: toDisplayValue(item.numberingPlaceDesc),
      plateId: toDisplayValue(item.plateId),
      plateNum: toDisplayValue(item.plateNum),
      plateStatus: {
        description: toDisplayValue(item.plateStatus?.description, ""),
        id: item.plateStatus?.id,
      },
    }));
  const noPlateResponses = rawPlateRecords.filter(
    (item) =>
      !item.convertedPlateNum && (item.status || item.title || item.detail),
  );
  const serviceErrorMessage =
    toDisplayValue((responsePayload as RawPlateResponse)?.msg, "") ||
    toDisplayValue((responsePayload as RawPlateResponse)?.message, "");
  const serviceErrorCode = toDisplayValue(
    (responsePayload as RawPlateResponse)?.code,
    "",
  );
  const serviceErrorContent = [
    ...(serviceErrorCode ? [{ label: "کد خطا", value: serviceErrorCode }] : []),
    { label: "پیام سرویس", value: serviceErrorMessage },
  ];
  const servicePdfFee = (
    data as { inquery?: { pdf_fee?: string | number | null } }
  ).inquery?.pdf_fee;
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number | null>(
    null,
  );
  const { openModal, closeModal, setOpenModal } = useModalStore();
  const {
    isPending: isFileSubmitting,
    mutate: purchaseFileFn,
    error: purchaseFileError,
    reset: resetPurchaseFile,
  } = usePurchaseFileMutation(session?.user.token);

  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timerEndTime, setTimerEndTime] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { setOpenToast } = useToastStore();
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const downloadPdfRef = useRef<DownloadPdfRef>(null);
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("plateInquiry.nationalCode"),
      value: plateInput.national_code,
    },
  ];

  const resultContent = plateData.map((item) => {
    const baseFields = [
      {
        label: t("plateInquiry.plate"),
        value: item.convertedPlateNum,
      },
      {
        label: t("plateInquiry.plateNO"),
        value: item.plateId,
      },
      {
        label: "شماره پلاک خام",
        value: item.plateNum,
      },
      {
        label: t("plateInquiry.numberingPlaceDesc"),
        value: item.numberingPlaceDesc || "نامشخص",
      },
      {
        label: "کد محل شماره‌گذاری",
        value: item.numberingPlaceCode,
      },
      {
        label: t("plateInquiry.isFak"),
        value: !item.detachDate
          ? t("plateInquiry.active")
          : t("plateInquiry.inactive"),
      },
    ];

    // Only add fakDate field when is_FAK is "1"
    if (item.detachDate) {
      baseFields.splice(5, 0, {
        label: t("plateInquiry.fakDate"),
        value: `${toJalali(item.detachDate)} - ${extractTime(item.detachDate)}`,
      });
    }

    // Only add description field when it exists and is not empty
    if (item.plateStatus.description.trim() !== "") {
      baseFields.push({
        label: t("plateInquiry.description"),
        value: item.plateStatus.description,
      });
    }

    if (item.plateStatus.id !== undefined) {
      baseFields.push({
        label: "کد وضعیت پلاک",
        value: String(item.plateStatus.id),
      });
    }

    return baseFields;
  });
  const noPlateContent = noPlateResponses.map((item) => [
    { label: "وضعیت پاسخ", value: toDisplayValue(item.status) },
    { label: "نوع پاسخ", value: toDisplayValue(item.type) },
    { label: "عنوان", value: toDisplayValue(item.title) },
    { label: "جزئیات", value: toDisplayValue(item.detail) },
    {
      label: "ثبت گزارش در سرور",
      value:
        item.logOnServer === undefined ? "-" : item.logOnServer ? "بله" : "خیر",
    },
  ]);

  const handleCloseModal = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("download");
    url.searchParams.delete("payment");
    window.history.replaceState({}, "", url.toString());
    closeModal();
  };
  const handleTimerComplete = () => {
    setIsTimerActive(false);
    setTimerEndTime(null);
  };
  const downloadPaymentSubmission = () => {
    purchaseFileFn(
      { payload: { purchase_id: data.id } },
      {
        onSuccess: (data) => {
          if (data.success) {
            setOpenToast({
              variant: "success",
              message: "خرید با موفقیت انجام شد",
              position: "top-center",
            });
            setTimeout(() => {
              // setOpenToast({
              //   variant: "success",
              //   message: "شروع دانلود فایل",
              //   position: "top-center",
              // });
              downloadPdfRef.current?.download();
            }, 1000);
            handleCloseModal();
          }
          setError(null);
          setIsTimerActive(false);
          setTimerEndTime(null);
        },
        onError: (error) => {
          if (error) {
            setError(error.message);
            setIsTimerActive(true); // Start timer
            setTimerEndTime(Date.now() + 60000);
          }
        },
      },
    );
  };

  useEffect(() => {
    const shouldDownload = searchParams?.get("download");
    if (shouldDownload) {
      setOpenModal("vehicle-payment-method");
      // if (shouldDownload === "true") {
      //   // Small delay to ensure component is fully mounted
      //   setTimeout(() => {
      //     downloadPaymentSubmission();
      //   }, 500);
      // } else {
      //   setOpenModal("vehicle-payment-method");
      // }
    }
  }, [searchParams]);
  return (
    <div className="shadow-main from-sky-bliss relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          {t("plateInquiry.title")}
        </h2>
        <div className="mb-8 w-full">
          {plateData.length ? (
            plateData.map((item, index) => (
              <DetailPlateInquiry
                key={`${item.plateId}-${index}`}
                plateData={item}
                isOpen={openAccordionIndex === index}
                plateInput={plateInput}
                onToggle={() =>
                  setOpenAccordionIndex(
                    openAccordionIndex === index ? null : index,
                  )
                }
              />
            ))
          ) : (
            <p className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center text-sm leading-7 text-amber-800">
              {noPlateResponses[0]?.title ||
                noPlateResponses[0]?.detail ||
                serviceErrorMessage ||
                "اطلاعاتی از پلاک فعال برای این کد ملی یافت نشد."}
            </p>
          )}
        </div>
        <InputDataContent
          content={inputContent}
          className="le p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("accountToIban.resultTitle")}
          </h2>
          <DownloadPdf
            className="hidden"
            ref={downloadPdfRef}
            id="plate-inquiry-download-pdf"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            fileName="پلاک فعال خودرو"
            tableTitle="پلاک فعال خودرو"
          />
        </FlexBetween>
        {resultContent.length ? (
          resultContent.map((item, index) => (
            <ResultContent content={item} key={`plate-result-${index}`} />
          ))
        ) : noPlateContent.length ? (
          noPlateContent.map((item, index) => (
            <ResultContent
              content={item}
              key={`plate-not-found-result-${index}`}
              title="نتیجه استعلام"
            />
          ))
        ) : serviceErrorMessage ? (
          <ResultContent title="خطای استعلام" content={serviceErrorContent} />
        ) : null}
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <CustomButton
            id="details-khalafi-inquiry-download"
            className="order-2 flex w-fit cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            onClick={() => setOpenModal("vehicle-payment-method")}
          >
            دانلود نتیجه
          </CustomButton>
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-plate-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-plate-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Plate_Inquiry.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          backgroundColor: "white",
          minHeight: "100vh",
          direction: "rtl",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "1.5rem",
            fontFamily: "IRANYekan, sans-serif",
            direction: "rtl",
            border: "2px solid #5a4bd2",
            borderRadius: "12px",
            margin: "1rem",
            minHeight: "calc(100vh - 2rem)",
            boxSizing: "border-box",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "0",
              left: "0",
              right: "0",
              bottom: "0",
              backgroundImage: `url("${logoImage.src}")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center center",
              backgroundSize: "contain",
              opacity: 0.04,
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <div
              style={{
                marginBottom: "0.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <Logo width={30} height={30} src={logoImage} className="mt-6" />
              <span
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "bold",
                  lineHeight: 1,
                }}
              >
                سامانه استعلام نوین
              </span>
            </div>
            <p style={{ fontSize: "1rem", color: "#6A7282", margin: "0" }}>
              استعلام پلاک فعال خودرو
            </p>
          </div>

          {plateData.map((item, index) => (
            <div
              key={index}
              style={{ marginBottom: "1.5rem", pageBreakInside: "avoid" }}
            >
              <DetailPlateInquiryPdf
                plateInput={plateInput}
                plateData={item}
                isOpen={true}
              />
            </div>
          ))}
          {resultContent.map((item, index) => (
            <ResultContent content={item} key={index} forPdf />
          ))}
          {noPlateContent.map((item, index) => (
            <ResultContent
              content={item}
              key={`plate-not-found-pdf-${index}`}
              title="نتیجه استعلام"
              forPdf
            />
          ))}
          {serviceErrorMessage && !noPlateContent.length && (
            <ResultContent
              title="خطای استعلام"
              content={serviceErrorContent}
              forPdf
            />
          )}
          {/* Footer on every page */}
          <div
            style={{
              width: "100%",
              marginTop: "auto",
              position: "relative",
              zIndex: 50,
            }}
          >
            <hr />
            <footer
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                padding: "1rem",
              }}
            >
              <p style={{ margin: 0, fontSize: "14px", lineHeight: "30px" }}>
                این گزارش توسط سامانه استعلامات نوین وان (novin.one) تولید شده
                است.
                <br />
                اعتبار این استعلام را می‌توانید در بخش{" "}
                <b>"تأیید اصالت گزارش"</b> سایت بررسی کنید.
              </p>
              {data.code && (
                <div style={{ display: "flex" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span style={{ color: "#AAAAAA", fontSize: "14px" }}>
                      شناسه اصالت گزارش
                    </span>
                    <span
                      className="text-en"
                      style={{
                        color: "#D32F2F",
                        fontWeight: "bold",
                        fontSize: "26px",
                        letterSpacing: "1px",
                      }}
                    >
                      {data.code}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      position: "relative",
                    }}
                  >
                    <div style={{ width: "100px", height: "100px" }}>
                      <QRCodeCanvas
                        value={`https://novin.one/inquiry-validity?code=${data.code}`}
                        size={100}
                        level="H"
                        marginSize={3}
                      />
                    </div>
                    <div
                      style={{
                        position: "absolute",
                        left: "-40px",
                        top: "50%",
                        transform: "translateY(-50%) rotate(-90deg)",
                        transformOrigin: "center center",
                        fontSize: "12px",
                        color: "#AAAAAA",
                        whiteSpace: "nowrap",
                      }}
                    >
                      اسکن کنید
                    </div>
                  </div>
                </div>
              )}
            </footer>
          </div>
        </div>
      </div>
      {openModal === "vehicle-payment-method" && (
        <VehiclesPaymentModal
          payBtnId="payment-file-submit"
          submissionError={purchaseFileError}
          paymentSubmission={downloadPaymentSubmission}
          serviceCost={servicePdfFee}
          onClose={handleCloseModal}
          isOpen={openModal === "vehicle-payment-method"}
          submissionLoading={isFileSubmitting}
          onErrorReset={resetPurchaseFile}
          showManualError={error}
          isTimerActive={isTimerActive}
          timerEndTime={timerEndTime}
          onTimerComplete={handleTimerComplete}
        />
      )}
    </div>
  );
};

export default PlateInquiryDetails;
